/**
 * Owner "Walk plans" section for My Walks — each recurring plan with its child
 * walks grouped, a progress line, and a "Cancel plan" action (cancels the whole
 * remaining plan; individual upcoming walks are cancelled from the bookings list).
 */
import { CalendarRange, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatusPill, type StatusTone } from '@/components/ui/status-pill';
import { form_error_message } from '@/lib/forms';
import { use_my_walk_plans, use_cancel_walk_plan } from '@/features/walk-plan/hooks';
import type { WalkPlan, WalkPlanStatus } from '@/features/walk-plan/types';
import type { BookingStatus } from '@/features/booking/types';

const IST = 'Asia/Kolkata';
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const PLAN_TONE: Record<WalkPlanStatus, StatusTone> = {
  PENDING_PAYMENT: 'warning',
  ACTIVE: 'success',
  COMPLETED: 'info',
  CANCELLED: 'destructive',
  EXPIRED: 'warning',
};

const WALK_TONE: Record<BookingStatus, StatusTone> = {
  PENDING_PAYMENT: 'warning',
  AWAITING_VENDOR: 'warning',
  CONFIRMED: 'info',
  IN_PROGRESS: 'info',
  COMPLETED: 'success',
  CANCELLED: 'destructive',
  NO_SHOW: 'destructive',
};

/** A plan can still be cancelled while it has upcoming (non-started) walks. */
const CANCELLABLE_PLAN: WalkPlanStatus[] = ['PENDING_PAYMENT', 'ACTIVE'];

function walk_when(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', { timeZone: IST, weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function PlanCard({ plan, onCancel, cancelling }: { plan: WalkPlan; onCancel: (p: WalkPlan) => void; cancelling: boolean }) {
  const done = plan.walks.filter((w) => w.status === 'COMPLETED').length;
  return (
    <li data-testid={`my-plan-${plan.id}`}>
      <Card>
        <CardContent className="flex flex-col gap-3 p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold">
                  {[...plan.days_of_week].sort((a, b) => a - b).map((d) => DAY_LABELS[d]).join(', ')} · {plan.time}
                </span>
                <StatusPill tone={PLAN_TONE[plan.status]}>{plan.status.replace(/_/g, ' ').toLowerCase()}</StatusPill>
              </div>
              <p className="text-xs text-muted-foreground">
                {plan.weeks} week(s) from {plan.start_date} · {done}/{plan.total_walks} walks done
              </p>
            </div>
            <div className="text-right text-sm">
              <div className="font-heading font-bold tabular-nums">₹{plan.total_inr}</div>
              {plan.payment && (
                <div className="text-xs text-muted-foreground">{plan.payment.status === 'CAPTURED' ? 'Paid' : plan.payment.status.toLowerCase()}</div>
              )}
            </div>
          </div>

          <ul className="divide-y divide-border rounded-md border border-border" data-testid={`my-plan-walks-${plan.id}`}>
            {plan.walks.map((w) => (
              <li key={w.id} className="flex items-center justify-between px-3 py-2 text-sm">
                <span>{walk_when(w.slot_start)}</span>
                <StatusPill tone={WALK_TONE[w.status]}>{w.status.replace(/_/g, ' ').toLowerCase()}</StatusPill>
              </li>
            ))}
          </ul>

          {CANCELLABLE_PLAN.includes(plan.status) && (
            <div className="flex justify-end">
              <Button size="sm" variant="ghost" onClick={() => onCancel(plan)} disabled={cancelling} data-testid={`cancel-plan-${plan.id}`}>
                Cancel plan
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </li>
  );
}

export function WalkPlansSection() {
  const plans_q = use_my_walk_plans();
  const cancel_m = use_cancel_walk_plan();
  const plans = plans_q.data ?? [];

  async function cancel(plan: WalkPlan) {
    if (!window.confirm('Cancel this plan? All upcoming walks that haven’t started will be cancelled.')) return;
    const reason = window.prompt('Reason (optional):') ?? '';
    await cancel_m.mutateAsync({ id: plan.id, reason: reason.trim() || undefined });
  }

  if (plans_q.isLoading) {
    return (
      <div className="flex items-center gap-2 p-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading plans…
      </div>
    );
  }
  if (plans_q.error) {
    return <p className="p-2 text-sm text-destructive" data-testid="my-plans-error">{form_error_message(plans_q.error, 'Failed to load plans.')}</p>;
  }
  if (plans.length === 0) return null;

  return (
    <section className="flex flex-col gap-3" data-testid="my-walk-plans">
      <h2 className="flex items-center gap-2 font-heading text-lg font-bold">
        <CalendarRange className="h-5 w-5 text-primary" /> Walk plans
      </h2>
      <ul className="flex flex-col gap-3" data-testid="my-walk-plans-list">
        {plans.map((p) => (
          <PlanCard key={p.id} plan={p} onCancel={cancel} cancelling={cancel_m.isPending} />
        ))}
      </ul>
    </section>
  );
}
